import { useEffect } from "react";
import { useFetcher } from "react-router";

import type { loader } from "~/routes/resources/questionnaire-data";

import type { BenefitEntry, GoalEntry } from "./types";

// Client hook for the questionnaire's goal + benefit lists (SFCC-11290). Wraps
// `/resources/questionnaire-data` via `useFetcher` — the same server-fetch /
// thin-client-hook idiom as `use-fetch-lms.ts`. Loaded once on mount; the result
// feeds both the benefits-comparison table and answer→goal id-lookup.

const RESOURCE_URL = "/resources/questionnaire-data";

export interface QuestionnaireData {
  goals: GoalEntry[];
  benefits: BenefitEntry[];
  isLoading: boolean;
  error: string | null;
}

export function useQuestionnaireData(): QuestionnaireData {
  const fetcher = useFetcher<typeof loader>({ key: "questionnaire-data" });

  useEffect(() => {
    // `fetcher.load` is idempotent per key; guard so we only kick the initial load.
    if (fetcher.state === "idle" && !fetcher.data) {
      fetcher.load(RESOURCE_URL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fetcher.data?.error) {
      console.warn("[questionnaire-data] load error:", fetcher.data.error);
    }
  }, [fetcher.data?.error]);

  return {
    goals: (fetcher.data?.goals ?? []) as GoalEntry[],
    benefits: (fetcher.data?.benefits ?? []) as BenefitEntry[],
    isLoading: fetcher.state !== "idle" || !fetcher.data,
    error: fetcher.data?.error ?? null,
  };
}
